import React, { useMemo, useState } from 'react';
import { Stack, Typography, Alert, CircularProgress, Box, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { Insights, Assessment } from '@mui/icons-material';
import { CHART_VIEWS } from './ItemAnalytics';
import { createInsights, getInsightsForActivity, MIN_IMPACT_PERCENTAGE, MAX_INSIGHTS } from './createInsights';
import Insight from './Insight';

function CardCaseInsights({ groupingMaps, chartView, isLoading, summaryError, onInsightClick }) {
    const [selectedActivity, setSelectedActivity] = useState('all');
    const [selectedSections, setSelectedSections] = useState(['card', 'procedure', 'hospital', 'physician']);

    // Available sections for multi-select
    const availableSections = [
        { value: 'card', label: 'Cards' },
        { value: 'procedure', label: 'Procedures' },
        { value: 'hospital', label: 'Hospitals' },
        { value: 'physician', label: 'Physicians' }
    ];

    // Get available activities based on chart view
    const availableActivities = useMemo(() => {
        const activities = [{ value: 'all', label: 'All Activities' }];
        
        if (chartView === CHART_VIEWS.PICKLIST_VIEW) {
            activities.push(
                { value: 'run', label: 'Run' },
                { value: 'restock', label: 'Restock' },
                { value: 'trashed', label: 'Trashed' }
            );
        } else if (chartView === CHART_VIEWS.INVENTORY_VIEW) {
            activities.push(
                { value: 'picked', label: 'Picked' },
                { value: 'used', label: 'Used' }
            );
        } else {
            activities.push(
                { value: 'used', label: 'Used' }
            );
        }
        
        return activities;
    }, [chartView]);

    // Reset selected activity when chart view changes
    React.useEffect(() => {
        setSelectedActivity('all');
    }, [chartView]);

    const allInsights = useMemo(() => {
        return createInsights(groupingMaps, selectedSections, availableActivities.map(a => a.value));
    }, [groupingMaps, chartView, availableSections, availableActivities]);

    const insights = useMemo(() => {
        return getInsightsForActivity(selectedActivity, allInsights);
    }, [selectedActivity, allInsights]);

    if (isLoading) {
        return (
            <Stack spacing={2} alignItems="center" py={4}>
                <CircularProgress />
                <Typography>Loading insights...</Typography>
            </Stack>
        );
    }

    if (summaryError) {
        return (
            <Alert severity="error">
                Error loading insights: {summaryError.message || 'Please try again'}
            </Alert>
        );
    }

    return (
        <Stack spacing={3}>
            <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Insights sx={{ color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Key Insights
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        ({insights.length} high-impact contributors found)
                    </Typography>
                </Stack>
                
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel id="activity-filter-label">Filter by Activity</InputLabel>
                        <Select
                            labelId="activity-filter-label"
                            value={selectedActivity}
                            label="Filter by Activity"
                            onChange={(event) => setSelectedActivity(event.target.value)}
                        >
                            {availableActivities.map((activity) => (
                                <MenuItem key={activity.value} value={activity.value}>
                                    {activity.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel id="sections-filter-label">Show Groups</InputLabel>
                        <Select
                            labelId="sections-filter-label"
                            multiple
                            value={selectedSections}
                            label="Show Groups"
                            onChange={(event) => setSelectedSections(event.target.value)}
                            renderValue={(selected) => 
                                selected.map(value => 
                                    availableSections.find(s => s.value === value)?.label
                                ).join(', ')
                            }
                        >
                            {availableSections.map((section) => (
                                <MenuItem key={section.value} value={section.value}>
                                    <Checkbox checked={selectedSections.indexOf(section.value) > -1} />
                                    <ListItemText primary={section.label} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
            </Stack>

            {insights.length === 0 ? (
                <Alert severity="info" icon={<Assessment />}>
                    No significant insights found. Contributors need at least {MIN_IMPACT_PERCENTAGE}% impact to appear here.
                </Alert>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small" sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow className='tableHeader'>
                                <TableCell sx={{ fontWeight: 600 }}>Rank</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Activity</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Value</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Impact</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {insights
                                .sort((a, b) => b.percentage - a.percentage)
                                .map((insight, index) => (
                                    <Insight
                                        key={insight.id}
                                        insight={insight}
                                        chartView={chartView}
                                        rank={index + 1}
                                        onInsightClick={onInsightClick}
                                    />
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Summary */}
            <Box
                sx={{
                    p: 2,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Typography variant="body2" color="text.secondary" align="center">
                    Showing contributors with ≥{MIN_IMPACT_PERCENTAGE}% impact • Max {MAX_INSIGHTS}
                </Typography>
            </Box>
        </Stack>
    );
}

export default CardCaseInsights;